/**
 * useDropImage — Hook para activar drag-and-drop sobre cualquier zona.
 *
 * Uso:
 *   const drop = useDropImage(async (file) => { await subir(file); });
 *   <div {...drop.bind} className={drop.dragging ? "border-amber-400" : ""}>...</div>
 *
 * - Acepta múltiples archivos en el mismo drop (se llaman uno a uno).
 * - Filtra por tipo: solo imágenes + PDF por defecto (configurable).
 * - `drop.dragging` indica visualmente cuando hay un archivo encima.
 */
import { useState, useCallback } from "react";

export default function useDropImage(onFile, opts = {}) {
  const {
    accept = ["image/", "application/pdf"],
    maxSizeMB = 20,
  } = opts;
  const [dragging, setDragging] = useState(false);

  const allowed = useCallback(
    (file) => {
      if (!file) return false;
      if (file.size > maxSizeMB * 1024 * 1024) return false;
      if (!accept || accept.length === 0) return true;
      return accept.some((a) => (file.type || "").startsWith(a) || a === "*");
    },
    [accept, maxSizeMB],
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  }, [dragging]);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Sólo desactivar si salimos del contenedor (no de sus hijos)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  }, []);

  const onDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      for (const f of files) {
        if (!allowed(f)) continue;
        try {
          await onFile(f);
        } catch (err) {
          console.error("useDropImage onFile error", err);
        }
      }
    },
    [allowed, onFile],
  );

  return {
    dragging,
    bind: { onDragOver, onDragLeave, onDrop },
  };
}
